/**
 * Flow Optimizer — queue REORDER mutation. Dragging queued rows into a new order
 * writes DENSE priorities (1..N, top = highest = promoted first) back onto each
 * element's bank record, patching ONLY the rows whose priority actually changed
 * and invalidating the queue once. Same bank routes as useQueuePriority.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer, QueueElement } from '@trafficpulse/shared';

import api from '@/services/api';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from './useFlowOptimizerQueue';
import { BANK_PATH } from './useQueuePriority';

/** Bank priority DTO caps at 10; a longer queue ties at 10 (engine tie-breaks by queuedAt). */
export const MAX_PRIORITY = 10;

export interface PriorityUpdate {
  bankId: string;
  priority: number;
}

/**
 * Dense 1..N priorities for the reordered list, returning ONLY the rows that
 * changed (so an unchanged tail isn't re-patched). Position 1 = highest.
 */
export function computePriorityUpdates(ordered: QueueElement[]): PriorityUpdate[] {
  const out: PriorityUpdate[] = [];
  ordered.forEach((el, index) => {
    const priority = Math.min(index + 1, MAX_PRIORITY);
    if (el.priority !== priority) out.push({ bankId: el.bankId, priority });
  });
  return out;
}

export interface ReorderQueueArgs {
  layer: FlowLayer;
  /** The queued elements in their NEW order (index 0 = top = priority 1). */
  ordered: QueueElement[];
}

export function useQueueReorder(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ layer, ordered }: ReorderQueueArgs) => {
      const updates = computePriorityUpdates(ordered);
      await Promise.all(
        updates.map((u) =>
          api.patch(`/voluum/${BANK_PATH[layer]}/${u.bankId}`, {
            priority: u.priority,
          }),
        ),
      );
      return updates;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) }),
  });
}
